import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Logo } from "../components/Logo";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { describeApiError } from "../lib/api";

export default function AdminLogin() {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(describeApiError(err, t.admin.invalid));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center bg-[#F8F9FA] px-6"
    >
      <div className="absolute top-6 right-6">
        <LanguageSwitcher variant="dark" />
      </div>

      <div className="w-full max-w-md">
        <div className="flex justify-center mb-10">
          <Logo />
        </div>

        <div className="bg-white border border-[#0D2F4C]/10 p-10">
          <div className="flex items-center gap-3 mb-2">
            <Lock size={18} className="text-[#0D2F4C]" strokeWidth={1.5} />
            <h1
              className="font-serif text-2xl text-[#0D2F4C]"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="admin-login-title"
            >
              {t.admin.login_title}
            </h1>
          </div>
          <p className="text-sm text-[#4B5563] mb-8">{t.admin.login_subtitle}</p>

          <form onSubmit={onSubmit} className="space-y-6">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#0D2F4C]/70 mb-2">
                {t.admin.email}
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) =>
                  setForm((f) => ({ ...f, email: e.target.value }))
                }
                required
                data-testid="admin-email-input"
                className="w-full px-0 py-2 bg-transparent border-b border-[#0D2F4C]/30 text-[#0D2F4C] focus:outline-none focus:border-[#0D2F4C]"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#0D2F4C]/70 mb-2">
                {t.admin.password}
              </label>
              <input
                type="password"
                value={form.password}
                onChange={(e) =>
                  setForm((f) => ({ ...f, password: e.target.value }))
                }
                required
                data-testid="admin-password-input"
                className="w-full px-0 py-2 bg-transparent border-b border-[#0D2F4C]/30 text-[#0D2F4C] focus:outline-none focus:border-[#0D2F4C]"
              />
            </div>

            {error && (
              <div
                data-testid="admin-login-error"
                className="p-3 bg-red-50 border border-red-200 text-red-800 text-sm"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-btn"
              className="w-full bg-[#0D2F4C] text-white py-3 text-sm tracking-wide hover:bg-[#0A243A] transition-colors disabled:opacity-50"
              style={{ borderRadius: 0 }}
            >
              {loading ? t.admin.logging_in : t.admin.login}
            </button>
          </form>
        </div>

        <div className="text-center mt-6">
          <a
            href="/"
            data-testid="admin-back-home"
            className="text-xs text-[#0D2F4C]/60 hover:text-[#0D2F4C] uppercase tracking-[0.2em]"
          >
            ← {t.nav.home}
          </a>
        </div>
      </div>
    </div>
  );
}
